import React, { useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import {
  NUMBER_FIELDS, ALL_NUMBER_FIELDS, HARD_FIELDS, blockLengthError, OPPOSITE_NOTE, topicRules, levelLabel, changesBetween, numberDiffers, numberError,
} from '../../constants/generatorTopics';

// Roster generator settings, one card per topic: title, one sentence, trade-off,
// level dropdown and the related number settings directly below.
// `meta` comes from GET /shifts/generator-settings: { rules, level_options,
// levels (defaults), factory_levels, numbers, factory_numbers, can_change_defaults }.

export { levelLabel };

const controlStyle = { width: '100%', minHeight: '44px', boxSizing: 'border-box' };
const cardStyle = {
  border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px',
  background: 'var(--white)', minWidth: 0,
};

export function ChangedPill({ title }) {
  return (
    <span
      title={title}
      style={{
        display: 'inline-block', marginLeft: '6px', padding: '0 7px', lineHeight: '14px', borderRadius: '999px',
        background: 'var(--primary-xlight)', border: '1px solid var(--primary)', color: 'var(--primary-dark)',
        fontSize: '0.68rem', fontWeight: 700, verticalAlign: 'middle', whiteSpace: 'nowrap',
      }}
    >
      Changed
    </span>
  );
}

// levelBaseline / numberBaseline: values a "Changed" marker compares against
// (factory values in Admin, the saved defaults for a single run).
// onNumbers: null → numbers are shown read-only with `numbersHint`.
export function GeneratorTopics({
  meta, levels, onLevels, numbers, onNumbers = null, levelBaseline, numberBaseline,
  baselineName = 'Factory value', numbersHint = null, disabled = false, idPrefix = 'gen',
}) {
  if (!meta?.rules || !levels) return null;

  // Number fields (editable) or a read-only line with a hint (Generate dialog)
  const numberBlock = (fields) => (
    onNumbers ? (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))', gap: '10px' }}>
        {fields.map((f) => {
          const nid = `${idPrefix}-num-${f.key}`;
          const changed = numberBaseline && numberDiffers(numbers[f.key], numberBaseline[f.key]);
          const err = numberError(f, numbers[f.key]) || (f.key === 'MAX_STREAK' ? blockLengthError(numbers) : null);
          return (
            <div key={f.key} style={{ minWidth: 0 }}>
              <label htmlFor={nid} style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px', lineHeight: 1.35 }}>
                {f.label}
                {changed && <ChangedPill title={`${baselineName}: ${numberBaseline[f.key]}`} />}
              </label>
              <input
                id={nid}
                type="number"
                inputMode="decimal"
                className="form-input"
                style={{ ...controlStyle, borderColor: err ? 'var(--danger)' : undefined }}
                min={f.min}
                max={f.max}
                step={f.step}
                value={numbers[f.key] ?? ''}
                disabled={disabled}
                aria-invalid={!!err}
                onChange={(e) => onNumbers({ ...numbers, [f.key]: e.target.value })}
              />
              <p style={{ margin: '4px 0 0', fontSize: '0.74rem', color: err ? 'var(--danger)' : 'var(--text-muted)', lineHeight: 1.4 }}>
                {err || f.hint}
              </p>
            </div>
          );
        })}
      </div>
    ) : (
      <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-light)', lineHeight: 1.45 }}>
        <span style={{ fontWeight: 600 }}>
          {fields.map((f) => f.short.replace('{v}', numbers[f.key] ?? '—')).join(' · ').replace(/^./, (c) => c.toUpperCase())}
        </span>
        {fields.some((f) => numberBaseline && numberDiffers(numbers[f.key], numberBaseline[f.key])) && (
          <ChangedPill title="Differs from the factory value" />
        )}
        {numbersHint && <><br /><span style={{ color: 'var(--text-muted)' }}>{numbersHint}</span></>}
      </p>
    )
  );

  const ruleCard = (rule) => {
    const id = `${idPrefix}-level-${rule.key}`;
    const options = meta.level_options.filter((o) => rule.allow_off || o.key !== 'off');
    const levelChanged = levelBaseline && levels[rule.key] !== levelBaseline[rule.key];
    const fields = NUMBER_FIELDS[rule.key] || [];
    return (
      <div key={rule.key} style={cardStyle}>
        <label htmlFor={id} style={{ display: 'block', fontWeight: 700, fontSize: '0.92rem', marginBottom: '4px' }}>
          {rule.label}
          {levelChanged && <ChangedPill title={`${baselineName}: ${levelLabel(meta, levelBaseline[rule.key])}`} />}
        </label>
        <p style={{ margin: '0 0 4px', fontSize: '0.82rem', lineHeight: 1.45 }}>{rule.help}</p>
        <p style={{ margin: '0 0 10px', fontSize: '0.78rem', lineHeight: 1.45, color: 'var(--text-muted)' }}>{rule.tradeoff}</p>
        <select
          id={id}
          className="form-select"
          style={controlStyle}
          value={levels[rule.key] || 'normal'}
          disabled={disabled}
          onChange={(e) => onLevels({ ...levels, [rule.key]: e.target.value })}
        >
          {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        {fields.length > 0 && numbers && (
          <div style={{ marginTop: '12px' }}>
            {levels[rule.key] === 'off' && (
              <p style={{ margin: '0 0 8px', fontSize: '0.76rem', color: 'var(--text-muted)' }}>Not used while Off.</p>
            )}
            {numberBlock(fields)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '12px' }}>
      {numbers && HARD_FIELDS.some((f) => numbers[f.key] != null) && (
        <div
          style={{
            gridColumn: '1 / -1', border: '1px solid var(--danger)', borderLeft: '4px solid var(--danger)',
            borderRadius: 'var(--radius)', padding: '12px 14px', background: 'var(--white)',
          }}
        >
          <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: '0.92rem' }}>Hard limit</p>
          <p style={{ margin: '0 0 10px', fontSize: '0.8rem', color: 'var(--text-light)', lineHeight: 1.45 }}>
            Every shift counts — regular shifts, events and the OM-Meeting — across weeks, months and plans. The generator and
            &quot;Fill open shifts&quot; never go over it (the OM-Meeting is still assigned, with a red warning).
          </p>
          <div style={{ maxWidth: '360px' }}>{numberBlock(HARD_FIELDS)}</div>
        </div>
      )}
      {topicRules(meta.rules).map((t) => (t.rule ? ruleCard(t.rule) : (
        <div
          key={t.group.map((r) => r.key).join('+')}
          style={{
            gridColumn: '1 / -1', border: '1px dashed var(--primary)', borderRadius: 'var(--radius)',
            padding: '10px', background: 'var(--primary-xlight)',
          }}
        >
          <p style={{ margin: '0 2px 10px', fontSize: '0.8rem', color: 'var(--primary-dark)', fontWeight: 600, lineHeight: 1.45 }}>
            ⇄ {OPPOSITE_NOTE}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: '10px' }}>
            {t.group.map(ruleCard)}
          </div>
        </div>
      )))}
    </div>
  );
}

const confirmBox = {
  marginTop: '10px', padding: '10px 12px', borderRadius: 'var(--radius)',
  background: 'var(--primary-xlight)', border: '1px solid var(--primary)', fontSize: '0.84rem',
};

function ChangeList({ changes }) {
  return (
    <ul style={{ margin: '0 0 8px', paddingLeft: '18px' }}>
      {changes.map((c) => <li key={c.key}>{c.label}: {c.from} → <strong>{c.to}</strong></li>)}
    </ul>
  );
}

function ConfirmButtons({ busy, onYes, onNo, yes }) {
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      <button type="button" className="btn btn-primary btn-sm" style={{ minHeight: '40px' }} disabled={busy} onClick={onYes}>
        {busy ? 'Saving…' : yes}
      </button>
      <button type="button" className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }} disabled={busy} onClick={onNo}>
        Cancel
      </button>
    </div>
  );
}

// Admin → Roster generator: one "Save" (levels + numbers, one request) and
// "Reset to defaults" (factory values), both after showing what changes.
// Admins and superadmin. onSaved(response) after either action.
export function GeneratorDefaultsActions({ meta, levels, numbers, onSaved }) {
  const [confirm, setConfirm] = useState(null); // 'save' | 'reset'
  const [busy, setBusy] = useState(false);
  if (!meta?.can_change_defaults) return null;

  const saved = { levels: meta.levels, numbers: meta.numbers };
  const factory = { levels: meta.factory_levels, numbers: meta.factory_numbers };
  const pending = changesBetween(meta, saved, { levels, numbers });
  const toFactory = changesBetween(meta, saved, factory);
  const errors = [...ALL_NUMBER_FIELDS.map((f) => numberError(f, numbers?.[f.key])), blockLengthError(numbers)].filter(Boolean);

  const run = async (kind) => {
    setBusy(true);
    try {
      const res = kind === 'save'
        ? await api.put('/shifts/generator-defaults', {
          levels,
          numbers: Object.fromEntries(ALL_NUMBER_FIELDS.map((f) => [f.key, Number(numbers[f.key])])),
        })
        : await api.post('/shifts/generator-defaults/reset');
      toast.success(kind === 'save' ? 'Generator settings saved.' : 'Generator settings reset to factory values.');
      setConfirm(null);
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save the generator settings.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button" className="btn btn-primary btn-sm" style={{ minHeight: '40px' }}
          disabled={busy || pending.length === 0 || errors.length > 0}
          onClick={() => setConfirm('save')}
        >
          Save
        </button>
        <button type="button" className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }}
          disabled={busy} onClick={() => setConfirm('reset')}>
          Reset to defaults
        </button>
        {pending.length > 0 && !errors.length && (
          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            {pending.length} unsaved change{pending.length === 1 ? '' : 's'}
          </span>
        )}
        {errors.length > 0 && <span style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>Fix the marked number first.</span>}
      </div>
      {confirm === 'save' && (
        <div style={confirmBox}>
          <p style={{ margin: '0 0 6px', fontWeight: 600 }}>Save these settings for all future generations?</p>
          <ChangeList changes={pending} />
          <ConfirmButtons busy={busy} onYes={() => run('save')} onNo={() => setConfirm(null)} yes="Save" />
        </div>
      )}
      {confirm === 'reset' && (
        <div style={confirmBox}>
          <p style={{ margin: '0 0 6px', fontWeight: 600 }}>Reset all generator settings to the factory values?</p>
          {toFactory.length > 0 ? (
            <ChangeList changes={toFactory} />
          ) : (
            <p style={{ margin: '0 0 8px', color: 'var(--text-muted)' }}>The saved settings already are the factory values.</p>
          )}
          {pending.length > 0 && (
            <p style={{ margin: '0 0 8px', color: 'var(--text-muted)' }}>Unsaved changes on this page are discarded.</p>
          )}
          <ConfirmButtons busy={busy} onYes={() => run('reset')} onNo={() => setConfirm(null)} yes="Reset to defaults" />
        </div>
      )}
    </div>
  );
}

// Generate dialog: save the levels of this run as the new defaults (admin+).
// onSaved(newDefaults).
export function DefaultLevelActions({ meta, levels, onSaved }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!meta?.can_change_defaults) return null;

  const diff = changesBetween(meta, { levels: meta.levels }, { levels });

  const save = async () => {
    setBusy(true);
    try {
      const res = await api.put('/shifts/generator-levels', { levels });
      toast.success('Default levels saved.');
      setConfirm(false);
      onSaved?.(res.data.levels);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save the defaults.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: '12px' }}>
      <button type="button" className="btn btn-secondary btn-sm" style={{ minHeight: '40px' }}
        disabled={busy || diff.length === 0} onClick={() => setConfirm(true)}>
        Save these levels as default
      </button>
      {confirm && (
        <div style={confirmBox}>
          <p style={{ margin: '0 0 6px', fontWeight: 600 }}>Save these levels as the new default for all future generations?</p>
          <ChangeList changes={diff} />
          <ConfirmButtons busy={busy} onYes={save} onNo={() => setConfirm(false)} yes="Save as default" />
        </div>
      )}
    </div>
  );
}
